'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import ProfileStep1 from './ProfileStep1';
import ProfileStep2 from './ProfileStep2';
import ProfileStep3 from './ProfileStep3';
import ProfileLivePreview from './ProfileLivePreview';

export interface ProfileFormData {
  username: string;
  displayName: string;
  bio: string;
  location: string;
  instagram: string;
  youtube: string;
  twitter: string;
  website: string;
  avatarColor: string;
  avatarInitials: string;
}

const steps = [
  { id: 1, label: 'Basic Info', description: 'Name, username & bio' },
  { id: 2, label: 'Social Links', description: 'Connect your profiles' },
  { id: 3, label: 'Review & Publish', description: 'Go live!' },
];

export default function CreateProfileWizard() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();

  const form = useForm<ProfileFormData>({
    defaultValues: {
      username: 'priya.creates',
      displayName: 'Priya Sharma',
      bio: '✨ Content Creator & Lifestyle Blogger\n📍 Mumbai, India\n🎥 New video every Tuesday!',
      location: 'Mumbai, India',
      instagram: 'priya.creates',
      youtube: '@PriyaCreates',
      twitter: 'priya_creates',
      website: 'priyasharma.in',
      avatarColor: 'gradient-brand',
      avatarInitials: 'PS',
    },
  });

  const watchedValues = form.watch();

  const handleNext = async () => {
    let fieldsToValidate: (keyof ProfileFormData)[] = [];
    if (currentStep === 1) fieldsToValidate = ['username', 'displayName', 'bio'];
    if (currentStep === 2) fieldsToValidate = [];

    const valid = await form.trigger(fieldsToValidate);
    if (valid) setCurrentStep((s) => Math.min(s + 1, 3));
  };

  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 1));

  const handlePublish = async () => {
    setIsSubmitting(true);
    // BACKEND INTEGRATION: PUT /api/profile with form data
    await new Promise((r) => setTimeout(r, 1600));
    toast.success('Profile published! Your bio page is now live 🎉');
    await new Promise((r) => setTimeout(r, 800));
    router.push('/dashboard');
    setIsSubmitting(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-700 text-foreground">Edit Profile</h1>
        <p className="text-sm text-muted-foreground mt-1">Customize your bio page — changes reflect instantly on your public URL</p>
      </div>

      {/* Step progress */}
      <div className="bg-card rounded-2xl border border-border p-5 card-shadow">
        <div className="flex items-center gap-0">
          {steps.map((step, idx) => (
            <React.Fragment key={`step-${step.id}`}>
              <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center font-700 text-sm transition-all duration-300 ${
                    currentStep > step.id
                      ? 'bg-success text-white'
                      : currentStep === step.id
                      ? 'bg-primary text-white shadow-lg'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {currentStep > step.id ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    step.id
                  )}
                </div>
                <div className="text-center hidden sm:block">
                  <p className={`text-xs font-600 ${currentStep === step.id ? 'text-primary' : 'text-muted-foreground'}`}>
                    {step.label}
                  </p>
                  <p className="text-xs text-muted-foreground/60">{step.description}</p>
                </div>
              </div>
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-3 transition-all duration-300 ${currentStep > step.id ? 'bg-success' : 'bg-border'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Form */}
        <div className="xl:col-span-2">
          <div className="bg-card rounded-2xl border border-border card-shadow p-6">
            {currentStep === 1 && <ProfileStep1 form={form} />}
            {currentStep === 2 && <ProfileStep2 form={form} />}
            {currentStep === 3 && <ProfileStep3 formData={watchedValues} />}

            {/* Navigation buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border text-sm font-600 text-muted-foreground hover:text-foreground hover:bg-muted transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              {currentStep < 3 ? (
                <button
                  onClick={handleNext}
                  className="btn-primary flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-600"
                >
                  Continue
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={handlePublish}
                  disabled={isSubmitting}
                  className="btn-primary flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-600 disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Publishing…
                    </>
                  ) : (
                    <>
                      🚀 Publish Profile
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="xl:col-span-1">
          <ProfileLivePreview formData={watchedValues} />
        </div>
      </div>
    </div>
  );
}